import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, List, LayoutGrid } from "lucide-react";
import { api, formatError, resolveImg } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import ProductImagePicker from "@/components/ProductImagePicker";

const empty = { name: "", slug: "", description: "", cover_image: "", top_level: false, parent_id: null, highlighted: false };

function slugify(s) {
    return s.toLowerCase().trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
}

function sortByHierarchy(items) {
    const sorted = [];
    const tops = items.filter((c) => !c.parent_id);
    tops.forEach((t) => {
        sorted.push(t);
        items.filter((c) => c.parent_id === t.id).forEach((child) => sorted.push(child));
    });
    items.filter((c) => c.parent_id && !tops.find((t) => t.id === c.parent_id)).forEach((c) => sorted.push(c));
    return sorted;
}

export default function AdminCategories() {
    const t = useT();
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadingCover, setUploadingCover] = useState(false);
    const [confirmId, setConfirmId] = useState(null);
    const confirmTimer = useRef(null);
    useEffect(() => () => clearTimeout(confirmTimer.current), []);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [view, setView] = useState(() => localStorage.getItem("admin-categories-view") || "list");
    const setViewMode = (v) => { setView(v); localStorage.setItem("admin-categories-view", v); };

    const load = () => {
        api.get("/categories")
            .then((r) => {
                // Newest first (within each hierarchy level)
                const byNewest = [...r.data].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
                setItems(sortByHierarchy(byNewest));
            })
            .catch(() => {});
        api.get("/categories/product-counts").then((r) => setCounts(r.data || {})).catch(() => {});
    };

    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };

    const openEdit = (c) => {
        setEditing(c);
        setForm({
            name: c.name, slug: c.slug, description: c.description || "",
            cover_image: c.cover_image || "", top_level: !!c.top_level, parent_id: c.parent_id || null, highlighted: !!c.highlighted,
        });
        setOpen(true);
    };

    const uploadCover = async (file) => {
        setUploadingCover(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({ ...f, cover_image: data.url }));
            toast.success("Cover uploaded");
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Upload failed");
        } finally {
            setUploadingCover(false);
        }
    };

    const openPicker = () => setPickerOpen(true);

    const pickImage = (url) => {
        setForm((f) => ({ ...f, cover_image: url }));
        setPickerOpen(false);
        toast.success("Cover image selected");
    };

    const save = async () => {
        if (!form.name || !form.slug) { toast.error("Name and slug are required."); return; }
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/categories/${editing.id}`, form);
                toast.success("Collection updated");
            } else {
                await api.post("/categories", form);
                toast.success("Collection created");
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (c) => {
        // First click arms the delete; a second click within 3s confirms it.
        if (confirmId !== c.id) {
            setConfirmId(c.id);
            clearTimeout(confirmTimer.current);
            confirmTimer.current = setTimeout(() => setConfirmId(null), 3000);
            return;
        }
        clearTimeout(confirmTimer.current);
        setConfirmId(null);
        try {
            await api.delete(`/categories/${c.id}`);
            toast.success(t("admin.trash.moved"));
            load();
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Delete failed");
        }
    };

    const topLevelItems = items.filter((c) => !c.parent_id && (!editing || c.id !== editing.id));

    const visibleItems = items.filter((c) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q);
    });

    return (
        <div className="p-8 md:p-12" data-testid="admin-categories">
            <header className="flex items-center justify-between flex-wrap gap-4 mb-10">
                <div>
                    <p className="overline">{t("admin.cat.overline")}</p>
                    <h1 className="font-serif text-4xl md:text-5xl mt-2">{t("admin.cat.title")}</h1>
                </div>
                <button
                    onClick={openNew}
                    data-testid="new-category-btn"
                    className="inline-flex items-center gap-2 bg-melilux-gold text-melilux-bg px-6 py-3 text-[0.7rem] uppercase tracking-[0.28em] hover:bg-melilux-goldBright transition-colors"
                >
                    <Plus size={14} /> {t("admin.cat.new")}
                </button>
            </header>

            <div className="mb-6 flex items-center gap-3 flex-wrap">
                <input
                    type="text"
                    placeholder={t("admin.prod.search")}
                    className="editorial-input max-w-sm"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <div className="ml-auto flex items-center border border-white/15" data-testid="categories-view-toggle">
                    <button
                        onClick={() => setViewMode("list")}
                        title={t("admin.view.list")}
                        data-testid="categories-view-list"
                        className={`p-3 transition-colors ${view === "list" ? "text-melilux-gold bg-white/[0.05]" : "text-melilux-cream/50 hover:text-melilux-cream"}`}
                    >
                        <List size={16} strokeWidth={1.5} />
                    </button>
                    <button
                        onClick={() => setViewMode("grid")}
                        title={t("admin.view.grid")}
                        data-testid="categories-view-grid"
                        className={`p-3 transition-colors ${view === "grid" ? "text-melilux-gold bg-white/[0.05]" : "text-melilux-cream/50 hover:text-melilux-cream"}`}
                    >
                        <LayoutGrid size={16} strokeWidth={1.5} />
                    </button>
                </div>
            </div>

            {view === "list" && (
            <div className="border border-white/10 bg-melilux-bg" data-testid="categories-table">
                <table className="w-full text-sm">
                    <thead>
                    <tr className="text-left text-[0.65rem] uppercase tracking-[0.25em] text-melilux-cream/45 border-b border-white/10">
                        <th className="py-4 px-5">{t("admin.cat.col.collection")}</th>
                        <th className="py-4 px-5">{t("admin.cat.col.slug")}</th>
                        <th className="py-4 px-5 text-center">{t("admin.cat.col.products")}</th>
                        <th className="py-4 px-5">{t("admin.cat.col.description")}</th>
                        <th className="py-4 px-5">{t("admin.cat.col.nav")}</th>
                        <th className="py-4 px-5">{t("admin.cat.col.highlighted")}</th>
                        <th className="py-4 px-5 text-right">{t("admin.cat.col.actions")}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {items.length === 0 && (
                        <tr><td colSpan={7} className="text-center py-12 text-melilux-cream/50">{t("admin.cat.empty")}</td></tr>
                    )}
                    {visibleItems.map((c) => {
                        const isChild = !!c.parent_id;
                        const parent = isChild ? items.find((p) => p.id === c.parent_id) : null;
                        return (
                            <tr key={c.id} className={`border-b border-white/5 hover:bg-white/[0.02] ${isChild ? "bg-white/[0.01]" : ""}`} data-testid={`category-row-${c.slug}`}>
                                <td className="py-4 px-5">
                                    <div className="flex items-center gap-4">
                                        {c.cover_image && (
                                            <img src={resolveImg(c.cover_image)} alt="" className="w-14 h-14 object-cover shrink-0" />
                                        )}
                                        <div className={isChild ? "pl-5 border-l border-white/10" : ""}>
                                            {isChild && (
                                                <div className="text-[0.6rem] uppercase tracking-[0.22em] text-melilux-cream/35 mb-0.5">
                                                    ↳ {parent?.name}
                                                </div>
                                            )}
                                            <span className={`font-serif text-lg ${isChild ? "text-melilux-cream/75" : ""}`}>{c.name}</span>
                                        </div>
                                    </div>
                                </td>
                                <td className="py-4 px-5 text-melilux-cream/65 font-mono text-xs">{c.slug}</td>
                                <td className="py-4 px-5 text-center">
                                    <span className="inline-block min-w-[1.75rem] text-melilux-cream/85 tabular-nums">{counts[c.id] || 0}</span>
                                </td>
                                <td className="py-4 px-5 text-melilux-cream/65 max-w-md truncate">{c.description}</td>
                                <td className="py-4 px-5">
                                    {c.top_level
                                        ? <span className="text-[0.62rem] uppercase tracking-[0.22em] px-2 py-1 border border-emerald-500/30 text-emerald-300">Yes</span>
                                        : <span className="text-melilux-cream/30">—</span>}
                                </td>
                                <td className="py-4 px-5">
                                    {c.highlighted
                                        ? <span className="text-[0.62rem] uppercase tracking-[0.22em] px-2 py-1 border border-melilux-gold/40 text-melilux-gold">Yes</span>
                                        : <span className="text-melilux-cream/30">—</span>}
                                </td>
                                <td className="py-4 px-5 text-right">
                                    <div className="inline-flex gap-1">
                                        <button onClick={() => openEdit(c)} data-testid={`edit-category-${c.slug}`} className="p-2 hover:text-melilux-gold transition-colors">
                                            <Pencil size={15} strokeWidth={1.5} />
                                        </button>
                                        <button
                                            onClick={() => remove(c)}
                                            data-testid={`delete-category-${c.slug}`}
                                            title={confirmId === c.id ? t("admin.delete.again") : t("admin.delete.tip")}
                                            className={`p-2 transition-colors ${confirmId === c.id ? "text-destructive bg-destructive/15 ring-1 ring-destructive/50" : "hover:text-destructive"}`}
                                        >
                                            <Trash2 size={15} strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
            )}

            {view === "grid" && (
                <div data-testid="categories-grid-admin">
                    {visibleItems.length === 0 ? (
                        <p className="text-center py-12 text-melilux-cream/50 border border-white/10">{t("admin.cat.empty")}</p>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
                            {visibleItems.map((c) => {
                                const isChild = !!c.parent_id;
                                const parent = isChild ? items.find((p) => p.id === c.parent_id) : null;
                                return (
                                    <div key={c.id} className="border border-white/10 bg-melilux-bg flex flex-col" data-testid={`category-card-${c.slug}`}>
                                        <div className="relative aspect-[4/3] bg-melilux-surface">
                                            {c.cover_image && (
                                                <img src={resolveImg(c.cover_image, 800)} loading="lazy" alt="" className="absolute inset-0 w-full h-full object-contain" />
                                            )}
                                            {c.highlighted && (
                                                <span className="absolute top-2 left-2 text-[0.6rem] uppercase tracking-[0.22em] px-2 py-1 bg-melilux-gold text-melilux-bg">
                                                    ★
                                                </span>
                                            )}
                                            <span className="absolute bottom-2 right-2 text-[0.62rem] uppercase tracking-[0.22em] px-2 py-1 bg-black/80 border border-white/15 text-white tabular-nums">
                                                {counts[c.id] || 0} {t("admin.cat.col.products")}
                                            </span>
                                        </div>
                                        <div className="p-4 flex flex-col gap-1.5 flex-1">
                                            {isChild && (
                                                <span className="text-[0.6rem] uppercase tracking-[0.22em] text-melilux-cream/35">↳ {parent?.name}</span>
                                            )}
                                            <span className="font-serif text-lg leading-tight">{c.name}</span>
                                            <span className="font-mono text-xs text-melilux-cream/45">{c.slug}</span>
                                            <div className="flex items-center justify-between mt-auto pt-2">
                                                {c.top_level
                                                    ? <span className="text-[0.6rem] uppercase tracking-[0.22em] px-2 py-1 border border-emerald-500/30 text-emerald-300">{t("admin.cat.col.nav")}</span>
                                                    : <span />}
                                                <div className="inline-flex items-center">
                                                    <button onClick={() => openEdit(c)} data-testid={`edit-category-grid-${c.slug}`} className="p-2 hover:text-melilux-gold transition-colors">
                                                        <Pencil size={15} strokeWidth={1.5} />
                                                    </button>
                                                    <button
                                                        onClick={() => remove(c)}
                                                        data-testid={`delete-category-grid-${c.slug}`}
                                                        title={confirmId === c.id ? t("admin.delete.again") : t("admin.delete.tip")}
                                                        className={`p-2 transition-colors ${confirmId === c.id ? "text-destructive bg-destructive/15 ring-1 ring-destructive/50" : "hover:text-destructive"}`}
                                                    >
                                                        <Trash2 size={15} strokeWidth={1.5} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {open && (
                <div
                    className="fixed inset-0 z-50 bg-melilux-bg/85 backdrop-blur-sm flex items-center justify-center p-6"
                    onClick={() => setOpen(false)}
                    data-testid="category-modal"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-xl bg-melilux-bg border border-white/15 max-h-[92vh] flex flex-col"
                    >
                        <div className="overflow-y-auto min-h-0 p-8 md:p-10">
                        <p className="overline">{editing ? t("admin.cat.modal.edit.overline") : t("admin.cat.modal.new.overline")}</p>
                        <h2 className="font-serif text-3xl mt-3 mb-7">
                            {editing ? t("admin.cat.modal.edit.title") : t("admin.cat.modal.new.title")}
                        </h2>

                        <div className="space-y-6">
                            <Field label={t("admin.cat.field.name")}>
                                <input
                                    className="editorial-input"
                                    value={form.name}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setForm({ ...form, name: v, slug: editing ? form.slug : slugify(v) });
                                    }}
                                    data-testid="cat-name"
                                />
                            </Field>

                            <Field label={t("admin.cat.field.slug")}>
                                <input
                                    className="editorial-input"
                                    value={form.slug}
                                    onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                                    data-testid="cat-slug"
                                />
                            </Field>

                            <Field label={t("admin.cat.field.parent")}>
                                <select
                                    className="bg-transparent border-b border-white/25 focus:border-melilux-gold outline-none w-full py-3.5 text-sm text-melilux-cream"
                                    value={form.parent_id || ""}
                                    onChange={(e) => setForm({ ...form, parent_id: e.target.value || null })}
                                    data-testid="cat-parent"
                                >
                                    <option value="" className="bg-melilux-bg">{t("admin.cat.field.parent.none")}</option>
                                    {topLevelItems.map((c) => (
                                        <option key={c.id} value={c.id} className="bg-melilux-bg">{c.name}</option>
                                    ))}
                                </select>
                            </Field>

                            <Field label={t("admin.cat.field.description")}>
                                <textarea
                                    className="editorial-input"
                                    rows={3}
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    data-testid="cat-description"
                                />
                            </Field>

                            <Field label={t("admin.cat.field.cover")}>
                                {form.cover_image && (
                                    <img src={resolveImg(form.cover_image)} alt="" className="w-full max-h-48 object-cover mb-3" />
                                )}
                                <div className="flex items-center gap-3 flex-wrap">
                                    <label className="inline-block cursor-pointer border border-white/15 px-4 py-2 text-[0.65rem] uppercase tracking-[0.25em] hover:border-melilux-gold hover:text-melilux-gold transition-colors">
                                        {uploadingCover ? t("admin.cat.field.cover.uploading") : t("admin.cat.field.cover.upload")}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => e.target.files?.[0] && uploadCover(e.target.files[0])}
                                            data-testid="cat-cover-upload"
                                        />
                                    </label>
                                    <button
                                        type="button"
                                        onClick={openPicker}
                                        data-testid="cat-cover-from-products"
                                        className="inline-block border border-white/15 px-4 py-2 text-[0.65rem] uppercase tracking-[0.25em] hover:border-melilux-gold hover:text-melilux-gold transition-colors"
                                    >
                                        {t("admin.cat.field.cover.fromProducts")}
                                    </button>
                                    <input
                                        placeholder={t("admin.cat.field.cover.url")}
                                        className="editorial-input flex-1 min-w-[200px]"
                                        value={form.cover_image}
                                        onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                                        data-testid="cat-cover-url"
                                    />
                                </div>
                            </Field>

                            <Field label={t("admin.cat.field.nav")}>
                                <label className="inline-flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.top_level ?? false}
                                        onChange={(e) => setForm({ ...form, top_level: e.target.checked })}
                                        className="w-4 h-4 accent-melilux-gold"
                                        data-testid="cat-top-level"
                                    />
                                    <span className="text-sm text-melilux-cream/75">{t("admin.cat.field.nav.label")}</span>
                                </label>
                            </Field>
                            <Field label={t("admin.cat.field.highlighted")}>
                                <label className="inline-flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.highlighted ?? false}
                                        onChange={(e) => setForm({ ...form, highlighted: e.target.checked })}
                                        className="w-4 h-4 accent-melilux-gold"
                                        data-testid="cat-highlighted"
                                    />
                                    <span className="text-sm text-melilux-cream/75">
                                        {t("admin.cat.field.highlighted.label")}
                                    </span>
                                </label>
                            </Field>
                        </div>
                        </div>

                        <div className="shrink-0 px-8 md:px-10 py-5 border-t border-white/10 flex justify-between gap-3">
                            <button
                                onClick={() => setOpen(false)}
                                className="px-6 py-3 text-[0.7rem] uppercase tracking-[0.28em] border border-white/15 hover:border-melilux-cream/40 transition-colors"
                                data-testid="cat-cancel"
                            >
                                {t("admin.cat.cancel")}
                            </button>
                            <button
                                onClick={save}
                                disabled={saving}
                                data-testid="cat-save"
                                className="px-7 py-3 text-[0.7rem] uppercase tracking-[0.28em] bg-melilux-gold text-melilux-bg hover:bg-melilux-goldBright transition-colors disabled:opacity-60"
                            >
                                {saving ? t("admin.cat.saving") : editing ? t("admin.cat.save") : t("admin.cat.create")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ProductImagePicker
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onPick={pickImage}
            />
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-[0.65rem] uppercase tracking-[0.28em] text-melilux-cream/55">{label}</label>
            <div className="mt-1">{children}</div>
        </div>
    );
}